import orderBy from "lodash/orderBy";
import React, { useMemo } from "react";

import { Button } from "~/components/button";
import { Dropdown } from "~/components/dropdown";
import { IconBell } from "~/components/icon";
import { cx } from "~/tools/class-variance";
import { secondsToMilliseconds } from "~/tools/date-time";
import { ImmutableSet } from "~/tools/immutable-set";
import { prop } from "~/tools/prop";

import { useUserProfile } from "../../common/use-user-profile";
import { querySystemNotifications } from "../../queries";
import { useQuery } from "../../queries/react-query";
import { Link } from "../react/link";

import { SystemNotifications } from "./system-notifications";

const SYSTEM_NOTIFICATIONS_REFETCH_INTERVAL_SECONDS = 30;

export const SystemNotificationCenter: React.FC = () => {
	const { data } = useQuery({
		...querySystemNotifications,
		refetchInterval: secondsToMilliseconds(SYSTEM_NOTIFICATIONS_REFETCH_INTERVAL_SECONDS),
		select: (notifications) => orderBy(notifications, ["notificationTimeEpochMs"], ["desc"]),
	});

	const [systemNotificationsRead, updateUserProfile] = useUserProfile({
		select: prop("systemNotificationsRead"),
	});

	const hasUnreadNotifications = useMemo(() => {
		if (data == null) {
			return false;
		}

		return data.some(({ notificationId }) => !systemNotificationsRead?.has(notificationId));
	}, [data, systemNotificationsRead]);

	return (
		<Dropdown
			align={{ offset: [0, 0] }}
			destroyPopupOnHide={false}
			forceRender
			overlay={
				<>
					<header className="flex items-center justify-between gap-4 border-gray-300 border-b p-4">
						<h5 className="m-0 font-medium text-gray-900 text-sm">Notifications</h5>

						{hasUnreadNotifications && (
							<Link
								className="text-13 leading-4"
								el="button"
								onClick={() => {
									updateUserProfile((userProfile) => ({
										...userProfile,
										systemNotificationsRead: ImmutableSet.fromArray(data?.map(({ notificationId }) => notificationId)),
									}));
								}}
								theme="primary"
							>
								Mark all as read
							</Link>
						)}
					</header>

					<SystemNotifications
						// responsive height adjustments for different screen heights:
						// single max-height can cause inconsistent content height and popover jumping to the top if space is limited
						// caused by strange viewport calculations,
						className="max-h-[90vh] overflow-y-auto [@media(max-height:1080px)]:max-h-[85vh] [@media(max-height:320px)]:max-h-[65vh] [@media(max-height:480px)]:max-h-[70vh] [@media(max-height:640px)]:max-h-[75vh] [@media(max-height:800px)]:max-h-[80vh]"
						notifications={data}
					/>
				</>
			}
			overlayClassName={cx(
				"w-105 max-w-full overflow-hidden rounded-sm border border-gray-300 border-solid bg-white p-1 pt-0 shadow-lg",
			)}
			placement="bottomLeft"
			trigger={["click"]}
		>
			<Button className="relative" data-testid="system-notification-button" icon>
				<IconBell className="text-lg text-white" />
				{hasUnreadNotifications && (
					<div className="absolute top-1 right-1 size-1.5 rounded-full bg-core-icon-alert-primary" />
				)}
			</Button>
		</Dropdown>
	);
};
